import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import Icon from './icons';

interface HeaderProps {
  currentPage?: 'english' | 'chinese';
}

const Header: React.FC<HeaderProps> = ({ currentPage = 'english' }) => {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle language change
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = e.target.value;
    
    // Only navigate if the language is actually changing
    if (newLanguage !== language) {
      setLanguage(newLanguage);
      
      // Redirect to the appropriate landing page
      if (newLanguage === 'zh') {
        navigate('/cn');
      } else if (newLanguage === 'en') {
        navigate('/en');
      }
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="bg-white shadow-sm py-4 px-4 fixed w-full z-50">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center">
          <Logo size="large" textColor="text-gray-800" withText={false} />
          
          {/* Desktop Navigation Menu */}
          <nav className="hidden md:flex ml-8 space-x-8">
            <Link
              to="/"
              className="text-base font-medium text-gray-500 hover:text-gray-900"
            >
              {t('header.home')}
            </Link>
            <Link
              to={language === 'zh' ? '/home-care-cn' : '/home-care'}
              className="text-base font-medium text-gray-500 hover:text-gray-900"
            >
              {language === 'zh' ? '个人版' : 'For Patients'}
            </Link>
            <Link
              to="/security"
              className="text-base font-medium text-gray-500 hover:text-gray-900"
            >
              {t('header.security')}
            </Link>
            <Link
              to="/about"
              className="text-base font-medium text-gray-500 hover:text-gray-900"
            >
              {t('header.aboutUs')}
            </Link>
          </nav>
        </div>
        
        <div className="flex items-center">
          {/* Language Switcher */}
          <select
            value={language}
            onChange={handleLanguageChange}
            className="bg-white border border-gray-300 rounded-md px-3 py-1 text-sm"
          >
            <option value="zh">中文</option>
            <option value="en">English</option>
          </select>

          {/* Mobile Menu Button */}
          <button 
            type="button"
            className="ml-4 md:hidden text-gray-600 hover:text-gray-900"
            onClick={toggleMobileMenu}
          >
            <Icon iconName={mobileMenuOpen ? "Close" : "Menu"} size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden mt-4 pt-4 border-t border-gray-100">
          <nav className="flex flex-col space-y-4 px-2">
            <Link
              to="/"
              className="text-base font-medium text-gray-500 hover:text-gray-900 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.home')}
            </Link>
            <Link
              to={language === 'zh' ? '/home-care-cn' : '/home-care'}
              className="text-base font-medium text-gray-500 hover:text-gray-900 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {language === 'zh' ? '个人版' : 'For Patients'}
            </Link>
            <Link
              to="/security"
              className="text-base font-medium text-gray-500 hover:text-gray-900 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.security')}
            </Link>
            <Link
              to="/about"
              className="text-base font-medium text-gray-500 hover:text-gray-900 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.aboutUs')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header; 